// pages/OpportunityDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  MoreVertical,
  FileText,
  Paperclip,
  Activity,
  DollarSign,
  Users,
  Plus,
  CheckCircle,
  Target,
  Package,
  AlertCircle,
  Copy,
  Download,
  MessageSquare,
  Calendar,
  User,
  Building2,
  MapPin,
  Save,
  X as CloseIcon,
  Loader,
  ExternalLink,
  XCircle,
  PhoneCall,
  RefreshCw,
} from "lucide-react";
import apiService from "../../services/api";
import EditOpportunityModal from "../../components/EditOpportunityModal";
import CreateTaskModal from "../../components/CreateTaskModal";
import BookAppointmentModal from "../../components/BookAppointmentModal";
import QuoteButton from "../../components/QuoteButton";
import QuickQuoteModal from "../QuickQuoteModal";
import CallNotesTimeline from "../../components/CallNotesTimeline";
import ActivityFeed from "../../components/ActivityFeed";
import PhoneLink from "../../components/PhoneLink";
import { useTheme } from "../../contexts/ThemeContext";

const OpportunityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showBookAppointment, setShowBookAppointment] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [cancellationDetails, setCancellationDetails] = useState({
    cancelledAt: new Date().toISOString().slice(0, 16),
    cancellationReason: "",
  });

  // Related data
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [visits, setVisits] = useState([]);
  const [users, setUsers] = useState([]);

  // Note creation
  const [newNote, setNewNote] = useState("");
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [isPrivateNote, setIsPrivateNote] = useState(false);

  // Inline editing
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingField, setSavingField] = useState(false);
  const [salesVisits, setSalesVisits] = useState([]);

  const [activityItems, setActivityItems] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Sales Status definitions
  const salesStatuses = [
    { key: null, label: "--None--", color: "gray" },
    { key: "appointment_booked", label: "Appointment Booked", color: "blue" },
    { key: "not_sat", label: "NOT SAT", color: "red" },
    { key: "sat_non_qual", label: "SAT NON QUAL", color: "yellow" },
    { key: "sat_qual", label: "SAT QUAL", color: "green" },
    { key: "sold", label: "SOLD", color: "purple" },
    { key: "dead", label: "DEAD", color: "gray" },
    { key: "cancelled", label: "Cancelled", color: "red" },
  ];
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneName, setCloneName] = useState("");
  const [isCloning, setIsCloning] = useState(false);
  const [showQuickQuote, setShowQuickQuote] = useState(false);

  // Add this function to handle cloning
  const handleCloneOpportunity = async () => {
    try {
      setIsCloning(true);
      const response = await apiService.cloneOpportunity(
        id,
        cloneName.trim() || null,
      );

      if (response.success) {
        // Navigate to the new cloned opportunity
        navigate(`/opportunities/${response.data.id}`);
      }
    } catch (error) {
      console.error("Error cloning opportunity:", error);
      alert("Failed to clone opportunity");
    } finally {
      setIsCloning(false);
      setShowCloneModal(false);
      setCloneName("");
    }
  };
  useEffect(() => {
    fetchOpportunity();
    fetchNotes();
    fetchAttachments();
    fetchUsers();
    fetchAppointments();
    fetchVisits();
  }, [id]);
  useEffect(() => {
    if (activeTab === "activity") {
      fetchActivity();
    }
  }, [activeTab, id]);
  // Add fetch function
  const fetchVisits = async () => {
    try {
      const response = await apiService.getOpportunityVisits(id);
      setSalesVisits(response.data || []);
    } catch (error) {
      console.error("Error fetching visits:", error);
      setSalesVisits([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setUsers(response.data || response.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  const fetchOpportunity = async () => {
    try {
      setLoading(true);
      const response = await apiService.getOpportunity(id);
      setOpportunity(response.data);
    } catch (error) {
      console.error("Error fetching opportunity:", error);
      navigate("/opportunities");
    } finally {
      setLoading(false);
    }
  };
  const fetchActivity = async () => {
    try {
      setActivityLoading(true);
      const response = await apiService.getOpportunityActivity(id);
      if (response?.success) {
        setActivityItems(response.data || []);
      }
    } catch (err) {
      console.error("Failed to load activity:", err);
    } finally {
      setActivityLoading(false);
    }
  };
  const fetchNotes = async () => {
    try {
      const response = await apiService.getOpportunityNotes(id);
      setNotes(response.data || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await apiService.getOpportunityAttachments(id);
      setAttachments(response.data || []);
    } catch (error) {
      console.error("Error fetching attachments:", error);
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await apiService.getOpportunityAppointments(id);
      setVisits(response.data || []);
    } catch (error) {
      console.error("Error fetching appointments:", error);
      setVisits([]);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this opportunity?")) {
      try {
        await apiService.deleteOpportunity(id);
        navigate("/opportunities");
      } catch (error) {
        console.error("Error deleting opportunity:", error);
        alert("Failed to delete opportunity");
      }
    }
  };

  const handleSalesStatusClick = (newStatus) => {
    setPendingStatus(newStatus);
    setShowStatusConfirmation(true);
  };

  const confirmStatusChange = async () => {
    if (!pendingStatus) return;

    try {
      const updateData = { salesStatus: pendingStatus };

      // Automatically set notSat to true when changing to 'not_sat' status
      if (pendingStatus === "not_sat") {
        updateData.notSat = true;
      }

      // Handle cancellation — attach date/reason and close the opportunity
      if (pendingStatus === "cancelled") {
        updateData.cancelledAt = cancellationDetails.cancelledAt
          ? new Date(cancellationDetails.cancelledAt).toISOString()
          : new Date().toISOString();
        if (cancellationDetails.cancellationReason) {
          updateData.cancellationReason =
            cancellationDetails.cancellationReason;
        }
        updateData.status = "lost";
        updateData.stage = "closed_lost";
      }

      await apiService.updateOpportunity(id, updateData);
      setShowStatusConfirmation(false);
      setPendingStatus(null);
      setCancellationDetails({
        cancelledAt: new Date().toISOString().slice(0, 16),
        cancellationReason: "",
      });
      await fetchOpportunity();
    } catch (error) {
      console.error("Error updating sales status:", error);
      alert("Failed to update sales status");
      setShowStatusConfirmation(false);
      setPendingStatus(null);
    }
  };

  const cancelStatusChange = () => {
    setShowStatusConfirmation(false);
    setPendingStatus(null);
    setCancellationDetails({
      cancelledAt: new Date().toISOString().slice(0, 16),
      cancellationReason: "",
    });
  };

  const handleCreateNote = async () => {
    if (!newNote.trim()) return;

    try {
      await apiService.createOpportunityNote(id, {
        title: newNoteTitle,
        body: newNote,
        isPrivate: isPrivateNote,
      });
      setNewNote("");
      setNewNoteTitle("");
      setIsPrivateNote(false);
      fetchNotes();
    } catch (error) {
      console.error("Error creating note:", error);
      alert("Failed to create note");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await apiService.uploadOpportunityAttachment(id, formData);
      fetchAttachments();
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file");
    }
  };

  const startEditing = (field, currentValue) => {
    setEditingField(field);

    // Handle date fields - convert to YYYY-MM-DD format for date inputs
    if (
      field.includes("Date") &&
      currentValue &&
      !field.includes("createdAt") &&
      !field.includes("updatedAt")
    ) {
      try {
        const date = new Date(currentValue);
        setEditingValue(date.toISOString().split("T")[0]);
      } catch (error) {
        setEditingValue(currentValue || "");
      }
    } else {
      setEditingValue(currentValue || "");
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditingValue("");
  };

  const saveFieldEdit = async (field) => {
    try {
      setSavingField(true);

      // Handle UUID fields - convert empty strings and "null" string to actual null
      let valueToSave = editingValue;

      // List of UUID fields that should accept null
      const uuidFields = ["assignedTo", "coachId", "accountId", "contactId"];

      if (uuidFields.includes(field)) {
        // Convert empty string, "null" string, or null to actual null
        if (
          editingValue === "" ||
          editingValue === "null" ||
          editingValue === null ||
          editingValue === "undefined"
        ) {
          valueToSave = null;
        }
      }

      // Special handling for telesalesPerson - it's stored as text, not UUID
      if (field === "telesalesPerson") {
        if (
          editingValue === "" ||
          editingValue === "null" ||
          editingValue === null
        ) {
          valueToSave = null;
        }
      }

      const updateData = { [field]: valueToSave };

      // Auto-tick checkboxes when dates are set
      if (field === "bookedDate" && valueToSave) {
        updateData.booked = true;
      } else if (field === "twoMinChallengeDate" && valueToSave) {
        updateData.twoMinChallenge = true;
      } else if (field === "satNonQualDate" && valueToSave) {
        updateData.nonQualSat = true;
      } else if (field === "qualSatDate" && valueToSave) {
        updateData.qualSat = true;
      } else if (field === "soldDate" && valueToSave) {
        updateData.sold = true;
      } else if (field === "installationDate" && valueToSave) {
        updateData.installComplete = true;
      }

      await apiService.updateOpportunity(id, updateData);
      await fetchOpportunity();
      setEditingField(null);
      setEditingValue("");
    } catch (error) {
      console.error("Error saving field:", error);
      alert("Failed to save changes");
    } finally {
      setSavingField(false);
    }
  };

  const toggleNotSat = async () => {
    try {
      setSavingField(true);
      const newValue = !opportunity.notSat;
      const updateData = { notSat: newValue };

      // Automatically set salesStatus to 'not_sat' when notSat is toggled to true
      if (newValue === true) {
        updateData.salesStatus = "not_sat";
      }

      await apiService.updateOpportunity(id, updateData);
      await fetchOpportunity();
    } catch (error) {
      console.error("Error toggling NOT SAT:", error);
      alert("Failed to update NOT SAT");
    } finally {
      setSavingField(false);
    }
  };

  const getSalesStatusColor = (status) => {
    const colors = {
      appointment_booked: "text-blue-400 bg-blue-400/10 border-blue-400/20",
      not_sat: "text-red-400 bg-red-400/10 border-red-400/20",
      sat_non_qual: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
      sat_qual: "text-green-400 bg-green-400/10 border-green-400/20",
      sold: "text-purple-400 bg-purple-400/10 border-purple-400/20",
      dead: "text-gray-400 bg-gray-400/10 border-gray-400/20",
      cancelled: "text-red-400 bg-red-400/10 border-red-400/20",
    };
    return colors[status] || "text-gray-400 bg-gray-400/10 border-gray-400/20";
  };

  const formatSalesStatus = (status) => {
    if (!status) return "None";
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getCurrentSalesStatusIndex = () => {
    if (!opportunity?.salesStatus) return -1;
    return salesStatuses.findIndex((s) => s.key === opportunity.salesStatus);
  };

  const getStatusChangeMessage = (status) => {
    const messages = {
      null: "Reset this opportunity to no status?",
      appointment_booked:
        "Mark this opportunity as Appointment Booked? This indicates a meeting has been scheduled.",
      not_sat:
        "Mark this opportunity as NOT SAT? This indicates the customer did not attend or was not satisfied.",
      sat_non_qual:
        "Mark this opportunity as SAT NON QUAL? This indicates the customer attended but doesn't qualify.",
      sat_qual:
        "Mark this opportunity as SAT QUAL? This indicates the customer attended and qualifies for the sale.",
      sold: "Mark this opportunity as SOLD? This will automatically create an Account and Contact record for this customer.",
      dead: "Mark this opportunity as DEAD? This indicates the opportunity is no longer viable.",
      cancelled:
        "Mark this opportunity as Cancelled? This indicates the opportunity was cancelled.",
    };
    return (
      messages[status] ||
      "Change the sales status? This will update the opportunity's progress."
    );
  };

  const formatCurrency = (amount, currency = "GBP") => {
    if (!amount && amount !== 0) return "—";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency || "GBP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (error) {
      return `${currency || "GBP"} ${parseFloat(amount).toFixed(0)}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return "—";
    }
  };

  const EditableField = ({
    label,
    field,
    value,
    type = "text",
    options = null,
    readOnly = false,
  }) => {
    const isEditing = editingField === field;
    const inputRef = React.useRef(null);

    // Position cursor at the end when editing starts
    React.useEffect(() => {
      if (isEditing && inputRef.current) {
        const input = inputRef.current;
        // For text inputs and textareas, move cursor to end
        // Skip setSelectionRange for select elements as they don't support it
        if (!options && (type === "text" || type === "textarea")) {
          const length = input.value.length;
          input.setSelectionRange(length, length);
        }
        input.focus();
      }
    }, [isEditing, type, options]);

    if (readOnly) {
      return (
        <div>
          <label
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
          >
            {label}
          </label>
          <p className={isDarkMode ? "text-white" : "text-gray-900"}>
            {value || "—"}
          </p>
        </div>
      );
    }

    return (
      <div className="group">
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              {options ? (
                <select
                  ref={inputRef}
                  value={editingValue === null ? "null" : editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                >
                  <option value="null">Select...</option>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : type === "textarea" ? (
                <textarea
                  ref={inputRef}
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 resize-none ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  rows={3}
                />
              ) : (
                <input
                  ref={inputRef}
                  type={type}
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
              )}
              <button
                onClick={() => saveFieldEdit(field)}
                disabled={savingField}
                className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {savingField ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
              </button>
              <button
                onClick={cancelEditing}
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <CloseIcon size={16} />
              </button>
            </div>
          ) : (
            <>
              {type === "tel" && value ? (
                <div className="flex-1">
                  <PhoneLink
                    phoneNumber={value}
                    entityType="opportunity"
                    entityId={id}
                    entityName={opportunity.name}
                  />
                </div>
              ) : (
                <p
                  className={`flex-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {value || "—"}
                </p>
              )}
              <button
                onClick={() => {
                  let rawValue = value;
                  if (field === "assignedTo") {
                    rawValue = opportunity.assignedTo;
                  } else if (field === "coachId") {
                    rawValue = opportunity.coachId;
                  } else if (field === "telesalesPerson") {
                    rawValue = opportunity.telesalesPerson;
                  } else if (type === "date" && value && value !== "—") {
                    rawValue = opportunity[field];
                  }
                  startEditing(field, rawValue);
                }}
                className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
              >
                <Edit
                  size={14}
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"} flex items-center justify-center`}
      >
        <Loader
          className={`${isDarkMode ? "text-white" : "text-gray-900"} animate-spin`}
          size={32}
        />
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"} flex items-center justify-center`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Opportunity not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"}`}
    >
      {/* Status Confirmation Modal */}
      {showStatusConfirmation && pendingStatus !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  pendingStatus === "dead" ||
                  pendingStatus === "cancelled" ||
                  pendingStatus === "not_sat"
                    ? "bg-red-500/10"
                    : pendingStatus === "sold"
                      ? "bg-purple-500/10"
                      : "bg-blue-500/10"
                }`}
              >
                {pendingStatus === "sold" ? (
                  <CheckCircle className="text-purple-400" size={24} />
                ) : pendingStatus === "dead" ||
                  pendingStatus === "cancelled" ||
                  pendingStatus === "not_sat" ? (
                  <XCircle className="text-red-400" size={24} />
                ) : (
                  <AlertCircle className="text-blue-400" size={24} />
                )}
              </div>
              <div className="flex-1">
                <h3
                  className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Confirm Status Change
                </h3>
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {getStatusChangeMessage(pendingStatus)}
                </p>
                <div
                  className={`mt-3 p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                >
                  <div className="flex items-center justify-between text-sm">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      Current Status:
                    </span>
                    <span
                      className={`px-2 py-1 rounded ${getSalesStatusColor(opportunity.salesStatus)}`}
                    >
                      {formatSalesStatus(opportunity.salesStatus)}
                    </span>
                  </div>
                  <div className="flex items-center justify-center my-2">
                    <ArrowLeft
                      className={`${isDarkMode ? "text-gray-600" : "text-gray-400"} rotate-180`}
                      size={20}
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      New Status:
                    </span>
                    <span
                      className={`px-2 py-1 rounded ${getSalesStatusColor(pendingStatus)}`}
                    >
                      {formatSalesStatus(pendingStatus)}
                    </span>
                  </div>
                </div>
                {pendingStatus === "sold" && !opportunity.account && (
                  <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Building2
                        size={16}
                        className="text-purple-400 mt-0.5 flex-shrink-0"
                      />
                      <div>
                        <p className="text-purple-400 text-xs font-medium">
                          Account & Contact Creation
                        </p>
                        <p className="text-purple-300 text-xs mt-1">
                          This will automatically create an Account and Contact
                          record for this customer.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {pendingStatus === "cancelled" && (
                  <div className="mt-3 space-y-3">
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="flex items-start gap-2">
                        <XCircle
                          size={16}
                          className="text-red-400 mt-0.5 flex-shrink-0"
                        />
                        <div>
                          <p className="text-red-400 text-xs font-medium">
                            Cascade Cancellation
                          </p>
                          <p className="text-red-300 text-xs mt-1">
                            The latest associated appointment will also be
                            marked as Cancelled using the same date and reason.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Cancelled At *
                      </label>
                      <input
                        type="datetime-local"
                        value={cancellationDetails.cancelledAt}
                        onChange={(e) =>
                          setCancellationDetails((prev) => ({
                            ...prev,
                            cancelledAt: e.target.value,
                          }))
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-red-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-red-500/40 text-white"
                            : "bg-white border-red-300 text-gray-900"
                        }`}
                      />
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Cancellation Reason
                      </label>
                      <textarea
                        value={cancellationDetails.cancellationReason}
                        onChange={(e) =>
                          setCancellationDetails((prev) => ({
                            ...prev,
                            cancellationReason: e.target.value,
                          }))
                        }
                        rows={3}
                        placeholder="Reason for cancellation..."
                        className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:border-red-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-red-500/40 text-white"
                            : "bg-white border-red-300 text-gray-900"
                        }`}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={cancelStatusChange}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  pendingStatus === "sold"
                    ? "bg-purple-600 hover:bg-purple-700"
                    : pendingStatus === "dead" ||
                        pendingStatus === "cancelled" ||
                        pendingStatus === "not_sat"
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        className={`border-b transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
      >
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <ArrowLeft size={20} />
              <span>Go Back</span>
            </button>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={() => setShowBookAppointment(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <Calendar size={16} />
                <span className="hidden sm:inline">Schedule Meeting</span>
              </button>

              <button
                onClick={() => setShowCreateTask(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <CheckCircle size={16} />
                <span className="hidden sm:inline">New Task</span>
              </button>

              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <Edit size={16} />
                <span className="hidden sm:inline">Edit</span>
              </button>
              <QuoteButton opportunity={opportunity} />

              {/* <button
                onClick={() => setShowQuickQuote(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                <FileText size={16} />
                <span className="hidden sm:inline">Quick Quote</span>
              </button> */}

              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                >
                  <MoreVertical size={20} />
                </button>

                {showActionsMenu && (
                  <div
                    className={`absolute right-0 top-12 w-48 border rounded-lg shadow-xl z-50 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                  >
                    <button
                      onClick={() => {
                        setCloneName(`${opportunity.name} (Copy)`);
                        setShowCloneModal(true);
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                    >
                      <Copy size={16} />
                      Clone Opportunity
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                    >
                      <Copy size={16} />
                      Copy Link
                    </button>
                    <button
                      onClick={() => {
                        window.print();
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                    >
                      <Download size={16} />
                      Export
                    </button>
                    <div
                      className={`border-t my-1 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    ></div>
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left flex items-center gap-2 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Opportunity Header Info */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                  <DollarSign size={24} />
                </div>
                <div>
                  <h1
                    className={`text-xl sm:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {opportunity.name}
                  </h1>
                  {opportunity.account && (
                    <div
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {opportunity.account.name}
                    </div>
                  )}
                </div>

                {opportunity.salesStatus && (
                  <span
                    className={`px-3 py-1 rounded-lg border text-sm font-medium ${getSalesStatusColor(
                      opportunity.salesStatus,
                    )}`}
                  >
                    {formatSalesStatus(opportunity.salesStatus)}
                  </span>
                )}
              </div>

              <div
                className={`flex flex-wrap items-center gap-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                <div className="flex items-center gap-2">
                  <DollarSign size={16} />
                  <span className="font-semibold text-green-400">
                    {formatCurrency(opportunity.amount, opportunity.currency)}
                  </span>
                </div>
                {opportunity.probability !== null && (
                  <div className="flex items-center gap-2">
                    <Target size={16} />
                    <span>{opportunity.probability}% Probability</span>
                  </div>
                )}
                {opportunity.expectedCloseDate && (
                  <div className="flex items-center gap-2">
                    <Calendar size={16} />
                    <span>
                      Close: {formatDate(opportunity.expectedCloseDate)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Opportunity Owner & Coach */}
            <div className="flex flex-row sm:flex-col gap-3 flex-wrap">
              {opportunity.assignedUser && (
                <div
                  className={`flex items-center gap-3 rounded-lg p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-700" : "bg-gray-300"}`}
                  >
                    {opportunity.assignedUser.profileImageUrl ? (
                      <img
                        src={opportunity.assignedUser.profileImageUrl}
                        alt={opportunity.assignedUser.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span
                        className={`text-lg font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
                      >
                        {opportunity.assignedUser.name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <div
                      className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Opportunity Owner
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {opportunity.assignedUser.name}
                    </div>
                  </div>
                </div>
              )}

              {opportunity.coach && (
                <div
                  className={`flex items-center gap-3 rounded-lg p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-700" : "bg-gray-300"}`}
                  >
                    {opportunity.coach.profileImageUrl ? (
                      <img
                        src={opportunity.coach.profileImageUrl}
                        alt={opportunity.coach.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span
                        className={`text-lg font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
                      >
                        {opportunity.coach.name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <div
                      className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Coach
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {opportunity.coach.name}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {opportunity.account && (
            <div className="mt-4 bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Building2 size={16} className="text-green-400" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Converted to Account
                  </p>
                  <p className="text-sm text-green-400 font-medium mt-0.5">
                    {opportunity.account.name}
                    {opportunity.account.accountNumber &&
                      ` (#${opportunity.account.accountNumber})`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!opportunity.account && opportunity.salesStatus !== "sold" && (
            <div className="mt-4 bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <AlertCircle size={16} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Not Yet an Active Customer
                  </p>
                  <p className="text-sm text-blue-400 mt-0.5">
                    Account and Contact will be created when this opportunity is
                    marked as SOLD
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Sales Status Path */}
          <div
            className={`mt-4 rounded-lg p-4 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"}`}
          >
            <h3
              className={`text-sm font-medium mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Sales Status
            </h3>

            {/* Mobile: compact grid */}
            <div className="grid grid-cols-2 gap-2 sm:hidden">
              {salesStatuses.map((status, index) => {
                const currentIndex = getCurrentSalesStatusIndex();
                const isActive = index === currentIndex + 1;
                const isCompleted =
                  currentIndex >= 0 && index <= currentIndex + 1;
                const isCurrent = index <= currentIndex + 1;
                return (
                  <button
                    key={status.key || "none"}
                    onClick={() => handleSalesStatusClick(status.key)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-left ${
                      isActive
                        ? "bg-blue-500/15 border-blue-500/50 text-white"
                        : isCompleted
                          ? "bg-green-500/10 border-green-500/30 text-white"
                          : isDarkMode
                            ? "bg-gray-800/50 border-gray-700 text-gray-400"
                            : "bg-white border-gray-200 text-gray-500"
                    }`}
                  >
                    <div
                      className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center border transition-all ${
                        isActive
                          ? "bg-blue-500 border-blue-500 text-white"
                          : isCompleted
                            ? "bg-green-500 border-green-500 text-white"
                            : isDarkMode
                              ? "bg-gray-700 border-gray-600 text-gray-400"
                              : "bg-gray-200 border-gray-300 text-gray-500"
                      }`}
                    >
                      {isCompleted && status.key ? (
                        <CheckCircle size={12} />
                      ) : (
                        <span className="text-xs font-semibold">{index}</span>
                      )}
                    </div>
                    <span
                      className={`text-xs font-medium leading-tight ${isCurrent ? (isDarkMode ? "text-white" : "text-gray-900") : isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {status.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Desktop: horizontal stepper */}
            <div className="hidden sm:flex items-center justify-between">
              {salesStatuses.map((status, index) => {
                const currentIndex = getCurrentSalesStatusIndex();
                const isActive = index === currentIndex + 1;
                const isCompleted =
                  currentIndex >= 0 && index <= currentIndex + 1;
                const isCurrent = index <= currentIndex + 1;

                return (
                  <React.Fragment key={status.key || "none"}>
                    <button
                      onClick={() => handleSalesStatusClick(status.key)}
                      className="flex-1 flex flex-col items-center gap-2 transition-all cursor-pointer hover:opacity-80"
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                          isActive
                            ? "bg-blue-500 border-blue-500 text-white"
                            : isCompleted
                              ? "bg-green-500 border-green-500 text-white"
                              : isDarkMode
                                ? "bg-gray-700 border-gray-600 text-gray-400"
                                : "bg-gray-200 border-gray-300 text-gray-500"
                        }`}
                      >
                        {isCompleted && status.key ? (
                          <CheckCircle size={20} />
                        ) : (
                          <span className="text-sm font-semibold">{index}</span>
                        )}
                      </div>
                      <div className="text-center">
                        <div
                          className={`text-xs font-medium ${
                            isCurrent
                              ? isDarkMode
                                ? "text-white"
                                : "text-gray-900"
                              : isDarkMode
                                ? "text-gray-400"
                                : "text-gray-500"
                          }`}
                        >
                          {status.label}
                        </div>
                      </div>
                    </button>
                    {index < salesStatuses.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 transition-all ${
                          currentIndex >= index
                            ? "bg-green-500"
                            : isDarkMode
                              ? "bg-gray-700"
                              : "bg-gray-300"
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {!opportunity.account && (
              <div
                className={`mt-3 text-xs text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Click on a status to update the opportunity's progress
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        {/* Lead Information */}
        {opportunity.lead && (
          <div
            className={`rounded-lg border p-4 mb-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3
                  className={`text-sm font-medium mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Lead Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label
                      className={`text-xs block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Lead Name
                    </label>
                    <button
                      onClick={() => navigate(`/leads/${opportunity.lead.id}`)}
                      className="text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1 text-sm"
                    >
                      {opportunity.lead.fullName}
                      <ExternalLink size={14} />
                    </button>
                  </div>
                  <div>
                    <label
                      className={`text-xs block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Company
                    </label>
                    <p
                      className={
                        isDarkMode
                          ? "text-white text-sm"
                          : "text-gray-900 text-sm"
                      }
                    >
                      {opportunity.lead.companyName || "—"}
                    </p>
                  </div>
                  <div>
                    <label
                      className={`text-xs block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Phone
                    </label>
                    {opportunity.lead.phone ? (
                      <PhoneLink
                        phoneNumber={opportunity.lead.phone}
                        entityType="lead"
                        entityId={opportunity.lead.id}
                        entityName={
                          opportunity.lead.firstName +
                          " " +
                          opportunity.lead.lastName
                        }
                        className="text-sm"
                      />
                    ) : (
                      <p
                        className={
                          isDarkMode
                            ? "text-gray-400 text-sm"
                            : "text-gray-500 text-sm"
                        }
                      >
                        —
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div
              className={`rounded-lg border ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className="tab-scroll flex border-b overflow-x-auto"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: isDarkMode
                    ? "#374151 #13161B"
                    : "#CBD5E1 #F1F5F9",
                }}
              >
                <style>{`
                  .tab-scroll::-webkit-scrollbar {
                    height: 3px;
                  }
                  .tab-scroll::-webkit-scrollbar-track {
                    background: ${isDarkMode ? "#13161B" : "#F1F5F9"};
                    border-radius: 0 0 8px 8px;
                  }
                  .tab-scroll::-webkit-scrollbar-thumb {
                    background: ${isDarkMode ? "#374151" : "#CBD5E1"};
                    border-radius: 4px;
                  }
                  .tab-scroll::-webkit-scrollbar-thumb:hover {
                    background: ${isDarkMode ? "#4B5563" : "#94A3B8"};
                  }
                `}</style>
                {[
                  { id: "details", label: "Details", icon: FileText },
                  { id: "contacts", label: "Contacts", icon: Users },
                  { id: "salesVisits", label: "Visits", icon: MapPin },
                  { id: "visits", label: "Appointments", icon: Calendar },
                  { id: "calls", label: "Call History", icon: PhoneCall },
                  { id: "quotations", label: "Quotations", icon: FileText },
                  { id: "quotes", label: "Contract", icon: FileText },
                  { id: "activity", label: "Activity", icon: Activity },
                  { id: "systemConfig", label: "System Config", icon: Package },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-shrink-0 px-4 sm:px-6 py-3 font-medium transition-colors ${
                        activeTab === tab.id
                          ? "text-blue-400 border-b-2 border-blue-400"
                          : isDarkMode
                            ? "text-gray-400 hover:text-white"
                            : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={16} />
                        {tab.label}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-4 sm:p-6">
                {/* Details Tab */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    {/* Opportunity Information */}
                    <div>
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <DollarSign size={18} />
                        Opportunity Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Opportunity Name"
                          field="name"
                          value={opportunity.name}
                        />
                        <EditableField
                          label="Account Name"
                          field="accountName"
                          value={opportunity.account?.name}
                          readOnly
                        />

                        {/* Opportunity Owner - Regular User */}
                        <EditableField
                          label="Opportunity Owner"
                          field="assignedTo"
                          value={opportunity.assignedUser?.name}
                          options={users.map((user) => ({
                            value: user.id,
                            label: user.name,
                          }))}
                        />

                        {/* Coach - Regular User */}
                        <EditableField
                          label="Coach"
                          field="coachId"
                          value={opportunity.coach?.name}
                          options={users.map((user) => ({
                            value: user.id,
                            label: user.name,
                          }))}
                        />

                        {/* Telesales Person - Regular User */}
                        <EditableField
                          label="Telesales Person"
                          field="telesalesPerson"
                          value={opportunity.telesalesPerson}
                          options={users.map((user) => ({
                            value: user.name, // Changed from user.id to user.name
                            label: user.name,
                          }))}
                        />

                        <EditableField
                          label="Type"
                          field="type"
                          value={opportunity.type}
                        />
                        <EditableField
                          label="Source"
                          field="source"
                          value={opportunity.source}
                        />
                        <EditableField
                          label="Sale Originated"
                          field="saleOriginated"
                          value={
                            opportunity.saleOriginated
                              ? ({
                                  cold_call: "Cold Call",
                                  reschedule: "Reschedule",
                                  email: "Email",
                                  base: "Base",
                                  website_enquiry: "Website Enquiry",
                                  incoming_call: "Incoming Call",
                                }[opportunity.saleOriginated] ??
                                opportunity.saleOriginated)
                              : null
                          }
                          options={[
                            { value: "cold_call", label: "Cold Call" },
                            { value: "reschedule", label: "Reschedule" },
                            { value: "email", label: "Email" },
                            { value: "base", label: "Base" },
                            {
                              value: "website_enquiry",
                              label: "Website Enquiry",
                            },
                            { value: "incoming_call", label: "Incoming Call" },
                          ]}
                        />
                        <EditableField
                          label="Close Date"
                          field="expectedCloseDate"
                          value={formatDate(opportunity.expectedCloseDate)}
                          type="date"
                        />
                        <EditableField
                          label="Points"
                          field="points"
                          value={opportunity.points}
                          type="number"
                        />
                        <EditableField
                          label="Stage"
                          field="stage"
                          value={opportunity.stage?.toUpperCase()}
                          readOnly
                        />
                        <EditableField
                          label="Additional Points"
                          field="additionalPoints"
                          value={opportunity.additionalPoints}
                          type="number"
                        />
                        <EditableField
                          label="Loss Reason"
                          field="lostReason"
                          value={opportunity.lostReason}
                        />
                        <EditableField
                          label="Referrer"
                          field="referrer"
                          value={opportunity.referrer}
                        />
                        <EditableField
                          label="Probability (%)"
                          field="probability"
                          value={opportunity.probability}
                          type="number"
                        />
                        <EditableField
                          label="Amount"
                          field="amount"
                          value={opportunity.amount}
                          type="number"
                        />
                        <EditableField
                          label="Funder"
                          field="funder"
                          value={opportunity.funder}
                        />
                        <EditableField
                          label="Lease Term"
                          field="leaseTerm"
                          value={opportunity.leaseTerm}
                        />
                        <EditableField
                          label="Lease Rental (Nett)"
                          field="leaseRentalNett"
                          value={opportunity.leaseRentalNett}
                        />
                        <EditableField
                          label="Follow Up ?"
                          field="followUp"
                          value={opportunity.followUp ? "Yes" : "No"}
                        />
                        <EditableField
                          label="Follow Up Date"
                          field="followUpDate"
                          value={formatDate(opportunity.followUpDate)}
                          type="date"
                        />
                        <EditableField
                          label="Phone Number"
                          field="phoneNumber"
                          value={opportunity.phoneNumber}
                          type="tel"
                        />
                      </div>
                    </div>

                    {/* Requirements */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Target size={18} />
                        Requirements
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Number of Handsets / Phone Extensions"
                          field="numberOfHandsets"
                          value={opportunity.numberOfHandsets}
                          type="number"
                        />
                        <EditableField
                          label="Monthly spend with Current Provider"
                          field="monthlySpendWithCurrentProvider"
                          value={
                            opportunity.monthlySpendWithCurrentProvider
                              ? formatCurrency(
                                  opportunity.monthlySpendWithCurrentProvider,
                                  opportunity.currency,
                                )
                              : null
                          }
                          type="number"
                        />
                        <EditableField
                          label="Current Phone System Age (Years)"
                          field="currentPhoneSystemAge"
                          value={opportunity.currentPhoneSystemAge}
                          type="number"
                        />
                        <EditableField
                          label="Contract Term Left (Years)"
                          field="contractTermLeft"
                          value={opportunity.contractTermLeft}
                          type="number"
                        />
                        <EditableField
                          label="Units Sold"
                          field="unitsSold"
                          value={opportunity.unitsSold}
                          type="number"
                        />
                        <EditableField
                          label="Contract end date"
                          field="contractEndDate"
                          value={formatDate(opportunity.contractEndDate)}
                          type="date"
                        />
                        <div className="sm:col-span-2">
                          <EditableField
                            label="Caller Notes"
                            field="callerNotes"
                            value={opportunity.callerNotes}
                            type="textarea"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Deal Score */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Deal Score
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div
                          className={`rounded-lg p-4 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <label
                              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              BOOKED
                            </label>
                            <CheckCircle
                              className={
                                opportunity.booked
                                  ? "text-green-400"
                                  : isDarkMode
                                    ? "text-gray-600"
                                    : "text-gray-400"
                              }
                              size={20}
                            />
                          </div>
                          <EditableField
                            label="Booked Date"
                            field="bookedDate"
                            value={formatDate(opportunity.bookedDate)}
                            type="date"
                          />
                        </div>

                        <div
                          className={`rounded-lg p-4 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <label
                              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              2 MIN CHALLENGE
                            </label>
                            <CheckCircle
                              className={
                                opportunity.twoMinChallenge
                                  ? "text-green-400"
                                  : isDarkMode
                                    ? "text-gray-600"
                                    : "text-gray-400"
                              }
                              size={20}
                            />
                          </div>
                          <EditableField
                            label="2 MIN CHALLENGE Date"
                            field="twoMinChallengeDate"
                            value={formatDate(opportunity.twoMinChallengeDate)}
                            type="date"
                          />
                        </div>

                        <div
                          className={`rounded-lg p-4 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <label
                              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              NON QUAL SAT
                            </label>
                            <CheckCircle
                              className={
                                opportunity.nonQualSat
                                  ? "text-green-400"
                                  : isDarkMode
                                    ? "text-gray-600"
                                    : "text-gray-400"
                              }
                              size={20}
                            />
                          </div>
                          <EditableField
                            label="SAT NON-QUAL Date"
                            field="satNonQualDate"
                            value={formatDate(opportunity.satNonQualDate)}
                            type="date"
                          />
                        </div>

                        <div
                          className={`rounded-lg p-4 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <label
                              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              QUAL SAT
                            </label>
                            <CheckCircle
                              className={
                                opportunity.qualSat
                                  ? "text-green-400"
                                  : isDarkMode
                                    ? "text-gray-600"
                                    : "text-gray-400"
                              }
                              size={20}
                            />
                          </div>
                          <EditableField
                            label="QUAL SAT Date"
                            field="qualSatDate"
                            value={formatDate(opportunity.qualSatDate)}
                            type="date"
                          />
                        </div>

                        <div
                          className={`rounded-lg p-4 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <label
                              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              SOLD
                            </label>
                            <CheckCircle
                              className={
                                opportunity.sold
                                  ? "text-green-400"
                                  : isDarkMode
                                    ? "text-gray-600"
                                    : "text-gray-400"
                              }
                              size={20}
                            />
                          </div>
                          <EditableField
                            label="Sold Date"
                            field="soldDate"
                            value={formatDate(opportunity.soldDate)}
                            type="date"
                          />
                        </div>

                        <div
                          className={`rounded-lg p-4 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <label
                              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Install Complete
                            </label>
                            <CheckCircle
                              className={
                                opportunity.installComplete
                                  ? "text-green-400"
                                  : isDarkMode
                                    ? "text-gray-600"
                                    : "text-gray-400"
                              }
                              size={20}
                            />
                          </div>
                          <EditableField
                            label="Installation Date"
                            field="installationDate"
                            value={formatDate(opportunity.installationDate)}
                            type="date"
                          />
                        </div>

                        <div
                          className={`rounded-lg p-4 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <label
                              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Upgrade Due
                            </label>
                            <CheckCircle
                              className={
                                opportunity.upgradeComplete
                                  ? "text-green-400"
                                  : isDarkMode
                                    ? "text-gray-600"
                                    : "text-gray-400"
                              }
                              size={20}
                            />
                          </div>
                          <div
                            className={`px-3 py-2 rounded-lg ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
                          >
                            <span
                              className={
                                opportunity.upgradeDueDate
                                  ? opportunity.upgradeComplete
                                    ? "text-green-400 font-semibold"
                                    : new Date(opportunity.upgradeDueDate) <
                                        new Date()
                                      ? "text-red-400 font-semibold"
                                      : "text-amber-400 font-semibold"
                                  : isDarkMode
                                    ? "text-gray-500"
                                    : "text-gray-400"
                              }
                            >
                              {opportunity.upgradeDueDate
                                ? new Date(opportunity.upgradeDueDate)
                                    .toLocaleDateString("en-GB", {
                                      month: "long",
                                      year: "numeric",
                                    })
                                    .toUpperCase()
                                : "—"}
                            </span>
                            {opportunity.upgradeComplete && (
                              <span className="ml-2 text-xs text-green-400">
                                (Complete)
                              </span>
                            )}
                          </div>
                        </div>

                        <div
                          className={`rounded-lg p-4 group ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <label
                              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              NOT SAT
                            </label>
                            <AlertCircle
                              className={
                                opportunity.notSat
                                  ? "text-red-400"
                                  : isDarkMode
                                    ? "text-gray-600"
                                    : "text-gray-400"
                              }
                              size={20}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={toggleNotSat}
                              disabled={savingField}
                              className={`flex-1 text-left px-3 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-200"}`}
                            >
                              <span
                                className={
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }
                              >
                                {savingField ? (
                                  <span className="flex items-center gap-2">
                                    <Loader
                                      size={16}
                                      className="animate-spin"
                                    />
                                    Updating...
                                  </span>
                                ) : opportunity.notSat ? (
                                  "Yes"
                                ) : (
                                  "No"
                                )}
                              </span>
                            </button>
                            <button
                              onClick={toggleNotSat}
                              disabled={savingField}
                              className="opacity-0 group-hover:opacity-100 p-2 hover:bg-gray-700 rounded transition-all disabled:opacity-50"
                            >
                              <Edit size={14} className="text-gray-400" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Additional Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Next Step"
                          field="nextStep"
                          value={opportunity.nextStep}
                        />
                        <EditableField
                          label="Lead Source"
                          field="leadSource"
                          value={opportunity.leadSource}
                        />
                        <div className="sm:col-span-2">
                          <EditableField
                            label="Description"
                            field="description"
                            value={opportunity.description}
                            type="textarea"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <div>
                            <label
                              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
                            >
                              Notes
                            </label>
                            <p
                              className={
                                isDarkMode ? "text-white" : "text-gray-900"
                              }
                            >
                              {notes.length} note(s)
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* System Information */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Created By
                          </label>
                          <p
                            className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <User size={16} />
                            {opportunity.creator?.name || "System"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Created Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(opportunity.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Last Modified By
                          </label>
                          <p
                            className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <User size={16} />
                            {opportunity.lastModifier?.name ||
                              opportunity.assignedUser?.name ||
                              "—"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Last Modified Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(opportunity.updatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contacts Tab */}
                {activeTab === "contacts" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3
                        className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Contact Roles
                      </h3>
                      <button className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm">
                        <Plus size={16} />
                        New
                      </button>
                    </div>
                    <div
                      className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <Users size={48} className="mx-auto mb-2 opacity-50" />
                      <p>No contact roles to display</p>
                    </div>
                  </div>
                )}

                {activeTab === "salesVisits" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3
                        className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Sales Visits ({salesVisits.length})
                      </h3>
                    </div>

                    {salesVisits.length > 0 ? (
                      <div className="space-y-3">
                        {salesVisits.map((visit) => (
                          <div
                            key={visit.id}
                            className={`rounded-lg border p-4 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700 hover:border-gray-600" : "bg-white border-gray-200 hover:border-gray-300"}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4
                                    className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {visit.visitName}
                                  </h4>
                                  {visit.outcome && (
                                    <span
                                      className={`px-2 py-0.5 text-xs rounded-full ${
                                        visit.outcome === "SOLD"
                                          ? "bg-purple-500/20 text-purple-400"
                                          : visit.outcome === "QUAL" ||
                                              visit.outcome === "SAT QUAL"
                                            ? "bg-green-500/20 text-green-400"
                                            : visit.outcome === "NON SAT" ||
                                                visit.outcome ===
                                                  "NOT INTERESTED"
                                              ? "bg-red-500/20 text-red-400"
                                              : "bg-yellow-500/20 text-yellow-400"
                                      }`}
                                    >
                                      {visit.outcome}
                                    </span>
                                  )}
                                </div>

                                {visit.vsistNotes && (
                                  <p
                                    className={`font-semibold mb-3 whitespace-pre-wrap ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {visit.vsistNotes}
                                  </p>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                  <div
                                    className={`flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                                  >
                                    <Calendar
                                      size={16}
                                      className={
                                        isDarkMode
                                          ? "text-gray-400"
                                          : "text-gray-500"
                                      }
                                    />
                                    <span>
                                      {new Date(
                                        visit.visitDate,
                                      ).toLocaleDateString("en-US", {
                                        weekday: "short",
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })}
                                    </span>
                                  </div>

                                  {visit.salesMember && (
                                    <div
                                      className={`flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                                    >
                                      <User
                                        size={16}
                                        className={
                                          isDarkMode
                                            ? "text-gray-400"
                                            : "text-gray-500"
                                        }
                                      />
                                      <span>{visit.salesMember.name}</span>
                                    </div>
                                  )}
                                </div>

                                {visit.appointment && (
                                  <div
                                    className={`mt-3 p-2 rounded text-sm ${isDarkMode ? "bg-gray-800/50 text-gray-400" : "bg-gray-100 text-gray-500"}`}
                                  >
                                    <strong
                                      className={
                                        isDarkMode
                                          ? "text-gray-300"
                                          : "text-gray-700"
                                      }
                                    >
                                      From Appointment:
                                    </strong>{" "}
                                    {visit.appointment.title}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        className={`text-center py-12 rounded-lg border ${isDarkMode ? "text-gray-400 bg-[#13161B] border-gray-700" : "text-gray-500 bg-white border-gray-200"}`}
                      >
                        <MapPin size={48} className="mx-auto mb-3 opacity-50" />
                        <p className="text-lg mb-2">No visits recorded</p>
                        <p className="text-sm">
                          Sales visits will appear here once completed
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Visits Tab */}
                {activeTab === "visits" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3
                        className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Appointments
                      </h3>
                      <button
                        onClick={() => setShowBookAppointment(true)}
                        className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                      >
                        <Plus size={16} />
                        Book Appointment
                      </button>
                    </div>

                    {visits.length > 0 ? (
                      <div className="space-y-3">
                        {visits.map((appointment) => (
                          <div
                            key={appointment.id}
                            className={`rounded-lg border p-4 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700 hover:border-gray-600" : "bg-white border-gray-200 hover:border-gray-300"}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4
                                    className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {appointment.title}
                                  </h4>
                                  <span
                                    className={`px-2 py-0.5 text-xs rounded-full ${
                                      appointment.status === "scheduled"
                                        ? "bg-blue-500/20 text-blue-400"
                                        : appointment.status === "confirmed"
                                          ? "bg-green-500/20 text-green-400"
                                          : appointment.status === "completed"
                                            ? "bg-purple-500/20 text-purple-400"
                                            : appointment.status === "cancelled"
                                              ? "bg-red-500/20 text-red-400"
                                              : "bg-gray-500/20 text-gray-400"
                                    }`}
                                  >
                                    {appointment.status
                                      .replace("_", " ")
                                      .toUpperCase()}
                                  </span>
                                  <span
                                    className={`px-2 py-0.5 text-xs rounded-full ${
                                      appointment.type === "meeting"
                                        ? "bg-blue-500/10 text-blue-400"
                                        : appointment.type === "call"
                                          ? "bg-green-500/10 text-green-400"
                                          : appointment.type === "site_visit"
                                            ? "bg-purple-500/10 text-purple-400"
                                            : appointment.type === "demo"
                                              ? "bg-orange-500/10 text-orange-400"
                                              : "bg-gray-500/10 text-gray-400"
                                    }`}
                                  >
                                    {appointment.type
                                      .replace("_", " ")
                                      .toUpperCase()}
                                  </span>
                                </div>

                                {appointment.description && (
                                  <p
                                    className={`text-sm mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    {appointment.description}
                                  </p>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                  <div
                                    className={`flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                                  >
                                    <Calendar
                                      size={16}
                                      className={
                                        isDarkMode
                                          ? "text-gray-400"
                                          : "text-gray-500"
                                      }
                                    />
                                    <span>
                                      {new Date(
                                        appointment.startTime.replace("Z", ""),
                                      ).toLocaleDateString("en-US", {
                                        weekday: "short",
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })}
                                    </span>
                                  </div>

                                  <div
                                    className={`flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                                  >
                                    <span>⏰</span>
                                    <span>
                                      {new Date(
                                        appointment.startTime.replace("Z", ""),
                                      ).toLocaleTimeString("en-US", {
                                        hour: "numeric",
                                        minute: "2-digit",
                                        hour12: true,
                                      })}
                                      {" - "}
                                      {new Date(
                                        appointment.endTime.replace("Z", ""),
                                      ).toLocaleTimeString("en-US", {
                                        hour: "numeric",
                                        minute: "2-digit",
                                        hour12: true,
                                      })}
                                    </span>
                                  </div>

                                  {appointment.location && (
                                    <div
                                      className={`flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                                    >
                                      <MapPin
                                        size={16}
                                        className={
                                          isDarkMode
                                            ? "text-gray-400"
                                            : "text-gray-500"
                                        }
                                      />
                                      <span className="truncate">
                                        {appointment.location}
                                      </span>
                                    </div>
                                  )}

                                  {appointment.assignee && (
                                    <div
                                      className={`flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                                    >
                                      <User
                                        size={16}
                                        className={
                                          isDarkMode
                                            ? "text-gray-400"
                                            : "text-gray-500"
                                        }
                                      />
                                      <span>{appointment.assignee.name}</span>
                                    </div>
                                  )}
                                </div>

                                {appointment.notes && (
                                  <div
                                    className={`mt-3 p-2 rounded text-sm ${isDarkMode ? "bg-gray-800/50 text-gray-400" : "bg-gray-100 text-gray-500"}`}
                                  >
                                    <strong
                                      className={
                                        isDarkMode
                                          ? "text-gray-300"
                                          : "text-gray-700"
                                      }
                                    >
                                      Notes:
                                    </strong>{" "}
                                    {appointment.notes}
                                  </div>
                                )}
                              </div>

                              <button
                                onClick={() =>
                                  navigate(`/appointments/${appointment.id}`)
                                }
                                className="text-blue-400 hover:text-blue-300 p-2"
                              >
                                <ExternalLink size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        className={`text-center py-12 rounded-lg border ${isDarkMode ? "text-gray-400 bg-[#13161B] border-gray-700" : "text-gray-500 bg-white border-gray-200"}`}
                      >
                        <Calendar
                          size={48}
                          className="mx-auto mb-3 opacity-50"
                        />
                        <p className="text-lg mb-2">
                          No appointments scheduled
                        </p>
                        <p className="text-sm mb-4">Book an appointment</p>
                        <button
                          onClick={() => setShowBookAppointment(true)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                        >
                          <Plus size={16} />
                          Book Appointment
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Call History Tab */}
                {activeTab === "calls" && (
                  <div>
                    <h3
                      className={`text-lg font-semibold flex items-center gap-2 mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      <PhoneCall size={18} />
                      Call History
                    </h3>
                    <CallNotesTimeline entityType="opportunity" entityId={id} />
                  </div>
                )}

                {/* Quotations Tab */}
                {activeTab === "quotations" && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3
                        className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <FileText size={18} />
                        Quotations
                      </h3>
                    </div>

                    {!opportunity?.customFields?.quotationUrl ? (
                      <div
                        className={`text-center py-12 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        <FileText
                          size={64}
                          className="mx-auto mb-4 opacity-30"
                        />
                        <p className="text-lg mb-2">No quotation available</p>
                        <p className="text-sm">
                          A quotation PDF has not been synced for this
                          opportunity yet
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Quotation PDF card */}
                        <div
                          className={`group flex items-center gap-4 p-4 rounded-lg transition-colors border ${isDarkMode ? "bg-gray-800/30 hover:bg-gray-800/50 border-gray-700/50" : "bg-gray-50 hover:bg-gray-100 border-gray-200"}`}
                        >
                          <div className="flex-shrink-0">
                            <FileText size={32} className="text-orange-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className={`font-medium text-sm truncate mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {opportunity.customFields.quotationNumber ??
                                "Quotation"}{" "}
                              {opportunity.customFields.quotationVersion !=
                                null && (
                                <span
                                  className={`text-xs ml-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  v{opportunity.customFields.quotationVersion}
                                </span>
                              )}
                            </div>
                            <div
                              className={`flex items-center gap-3 text-xs flex-wrap ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {opportunity.customFields.quotationCreatedAt && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={12} />
                                  Created:{" "}
                                  {new Date(
                                    opportunity.customFields.quotationCreatedAt,
                                  ).toLocaleDateString()}
                                </span>
                              )}
                              {opportunity.customFields.quotationSyncedAt && (
                                <>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <CheckCircle
                                      size={12}
                                      className="text-green-400"
                                    />
                                    <span className="text-green-400">
                                      Synced
                                    </span>
                                  </span>
                                </>
                              )}
                              {opportunity.customFields.quotationNotes && (
                                <>
                                  <span>•</span>
                                  <span className="italic">
                                    {opportunity.customFields.quotationNotes}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <a
                              href={opportunity.customFields.quotationUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                              title="View Quotation PDF"
                            >
                              <ExternalLink
                                size={18}
                                className="text-orange-400"
                              />
                            </a>
                          </div>
                        </div>

                        {/* Metadata grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                          {opportunity.customFields.quotationNumber && (
                            <div
                              className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/40" : "bg-gray-100"}`}
                            >
                              <p
                                className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Quote Number
                              </p>
                              <p
                                className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {opportunity.customFields.quotationNumber}
                              </p>
                            </div>
                          )}
                          {opportunity.customFields.quotationVersion !=
                            null && (
                            <div
                              className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/40" : "bg-gray-100"}`}
                            >
                              <p
                                className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Version
                              </p>
                              <p
                                className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                v{opportunity.customFields.quotationVersion}
                              </p>
                            </div>
                          )}
                          {opportunity.customFields.quoteConnectionId && (
                            <div
                              className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/40" : "bg-gray-100"}`}
                            >
                              <p
                                className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Connection ID
                              </p>
                              <p
                                className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {opportunity.customFields.quoteConnectionId}
                              </p>
                            </div>
                          )}
                          {opportunity.customFields.quotationSyncedAt && (
                            <div
                              className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/40" : "bg-gray-100"}`}
                            >
                              <p
                                className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Last Synced
                              </p>
                              <p
                                className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {new Date(
                                  opportunity.customFields.quotationSyncedAt,
                                ).toLocaleString()}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Contract Tab (formerly Quotes) */}
                {activeTab === "quotes" && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3
                        className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <FileText size={18} />
                        Contract
                      </h3>
                    </div>

                    {!opportunity?.customFields?.quoteConnectionId ? (
                      <div
                        className={`text-center py-12 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        <FileText
                          size={64}
                          className="mx-auto mb-4 opacity-30"
                        />
                        <p className="text-lg mb-2">No contract available</p>
                        <p className="text-sm">
                          A signed contract has not been received for this
                          opportunity yet
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Standard Contract row */}
                        <div
                          className={`group flex items-center gap-4 p-4 rounded-lg transition-colors border ${isDarkMode ? "bg-gray-800/30 hover:bg-gray-800/50 border-gray-700/50" : "bg-gray-50 hover:bg-gray-100 border-gray-200"}`}
                        >
                          <div className="flex-shrink-0">
                            <FileText size={32} className="text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className={`font-medium text-sm truncate mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              Standard Contract — Quote #
                              {opportunity.customFields.quoteConnectionId}
                            </div>
                            <div
                              className={`flex items-center gap-3 text-xs flex-wrap ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {opportunity.customFields.quoteCreatedAt && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={12} />
                                  Created:{" "}
                                  {new Date(
                                    opportunity.customFields.quoteCreatedAt,
                                  ).toLocaleDateString()}
                                </span>
                              )}
                              {opportunity.customFields.contractStatus && (
                                <>
                                  <span>•</span>
                                  <span
                                    className={`flex items-center gap-1 font-medium ${
                                      opportunity.customFields
                                        .contractStatus === "fully_signed"
                                        ? "text-green-400"
                                        : opportunity.customFields
                                              .contractStatus === "pending"
                                          ? "text-yellow-400"
                                          : "text-gray-400"
                                    }`}
                                  >
                                    <CheckCircle size={12} />
                                    {opportunity.customFields.contractStatus
                                      .replace(/_/g, " ")
                                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                                  </span>
                                </>
                              )}
                              {opportunity.customFields
                                .standardContractSigner && (
                                <>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <User size={12} />
                                    Signed by:{" "}
                                    {
                                      opportunity.customFields
                                        .standardContractSigner
                                    }
                                  </span>
                                </>
                              )}
                              {opportunity.customFields
                                .standardContractSignedAt && (
                                <>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <Calendar size={12} />
                                    Signed:{" "}
                                    {new Date(
                                      opportunity.customFields
                                        .standardContractSignedAt,
                                    ).toLocaleDateString()}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {opportunity.customFields.standardContractUrl && (
                              <a
                                href={
                                  opportunity.customFields.standardContractUrl
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                                title="View Standard Contract"
                              >
                                <ExternalLink
                                  size={18}
                                  className="text-blue-400"
                                />
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Rental Contract row */}
                        {(opportunity.customFields.rentalContractUrl ||
                          opportunity.customFields.rentalContractSigner ||
                          opportunity.customFields.rentalContractSignedAt) && (
                          <div
                            className={`group flex items-center gap-4 p-4 rounded-lg transition-colors border ${isDarkMode ? "bg-gray-800/30 hover:bg-gray-800/50 border-gray-700/50" : "bg-gray-50 hover:bg-gray-100 border-gray-200"}`}
                          >
                            <div className="flex-shrink-0">
                              <FileText size={32} className="text-purple-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div
                                className={`font-medium text-sm truncate mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                Rental Contract — Quote #
                                {opportunity.customFields.quoteConnectionId}
                              </div>
                              <div
                                className={`flex items-center gap-3 text-xs flex-wrap ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {opportunity.customFields.quoteCreatedAt && (
                                  <span className="flex items-center gap-1">
                                    <Calendar size={12} />
                                    Created:{" "}
                                    {new Date(
                                      opportunity.customFields.quoteCreatedAt,
                                    ).toLocaleDateString()}
                                  </span>
                                )}
                                {opportunity.customFields.contractStatus && (
                                  <>
                                    <span>•</span>
                                    <span
                                      className={`flex items-center gap-1 font-medium ${
                                        opportunity.customFields
                                          .contractStatus === "fully_signed"
                                          ? "text-green-400"
                                          : opportunity.customFields
                                                .contractStatus === "pending"
                                            ? "text-yellow-400"
                                            : "text-gray-400"
                                      }`}
                                    >
                                      <CheckCircle size={12} />
                                      {opportunity.customFields.contractStatus
                                        .replace(/_/g, " ")
                                        .replace(/\b\w/g, (c) =>
                                          c.toUpperCase(),
                                        )}
                                    </span>
                                  </>
                                )}
                                {opportunity.customFields
                                  .rentalContractSigner && (
                                  <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                      <User size={12} />
                                      Signed by:{" "}
                                      {
                                        opportunity.customFields
                                          .rentalContractSigner
                                      }
                                    </span>
                                  </>
                                )}
                                {opportunity.customFields
                                  .rentalContractSignedAt && (
                                  <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                      <Calendar size={12} />
                                      Signed:{" "}
                                      {new Date(
                                        opportunity.customFields
                                          .rentalContractSignedAt,
                                      ).toLocaleDateString()}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              {opportunity.customFields.rentalContractUrl && (
                                <a
                                  href={
                                    opportunity.customFields.rentalContractUrl
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                                  title="View Rental Contract"
                                >
                                  <ExternalLink
                                    size={18}
                                    className="text-purple-400"
                                  />
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "activity" && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h3
                        className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Activity size={18} className="text-blue-400" />
                        Activity Timeline
                      </h3>
                      <button
                        onClick={fetchActivity}
                        disabled={activityLoading}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${isDarkMode ? "bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-900"} disabled:opacity-50`}
                      >
                        <RefreshCw
                          size={13}
                          className={activityLoading ? "animate-spin" : ""}
                        />
                        Refresh
                      </button>
                    </div>

                    <ActivityFeed
                      items={activityItems}
                      loading={activityLoading}
                      emptyMessage="No activity has been recorded for this opportunity yet."
                      maxHeight="max-h-[600px]"
                    />
                  </div>
                )}

                {activeTab === "systemConfig" &&
                  (() => {
                    const sc = opportunity.customFields?.systemConfig;
                    if (!sc) {
                      return (
                        <div
                          className={`text-center py-12 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          <Package
                            size={40}
                            className="mx-auto mb-3 opacity-40"
                          />
                          <p className="font-medium">
                            No system config synced yet
                          </p>
                          <p className="text-sm mt-1">
                            Data will appear here once a system config is saved
                            on the quote platform.
                          </p>
                        </div>
                      );
                    }

                    const fmt = (v) =>
                      v === null || v === undefined ? "—" : v;
                    const fmtCurr = (v) =>
                      v != null ? `£${parseFloat(v).toFixed(2)}` : "—";

                    const Field = ({ label, value }) => (
                      <div>
                        <div
                          className={`text-xs mb-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {label}
                        </div>
                        <div
                          className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {fmt(value)}
                        </div>
                      </div>
                    );

                    const Section = ({ title, children }) => (
                      <div>
                        <h4
                          className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {title}
                        </h4>
                        <div
                          className={`rounded-lg border p-4 grid grid-cols-2 sm:grid-cols-3 gap-4 ${isDarkMode ? "bg-gray-800/30 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                        >
                          {children}
                        </div>
                      </div>
                    );

                    const hardwareWithQty = (sc.hardware_items || []).filter(
                      (h) => h.quantity > 0,
                    );
                    const additionalWithQty = (
                      sc.additional_hardware || []
                    ).filter((h) => h.quantity > 0);
                    const lc = sc.lease_calculator || {};
                    const charges = sc.service_charges || {};
                    const company = sc.company || {};
                    console.log("lc", lc);

                    return (
                      <div className="space-y-6">
                        {/* Synced at banner */}
                        {sc.synced_at && (
                          <div
                            className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${isDarkMode ? "bg-gray-800/50 text-gray-400" : "bg-gray-100 text-gray-500"}`}
                          >
                            <RefreshCw size={12} />
                            Last synced:{" "}
                            {new Date(sc.synced_at).toLocaleString()}
                            {sc.save_to_draft && (
                              <span className="ml-2 px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 font-medium">
                                Draft
                              </span>
                            )}
                          </div>
                        )}

                        {/* Company */}
                        {company.name && (
                          <Section title="Company">
                            <Field label="Company" value={company.name} />
                            <Field label="Director" value={company.md_name} />
                            <Field label="Position" value={company.position} />
                            <Field
                              label="Reg No."
                              value={company.company_reg}
                            />
                            <Field
                              label="Salesman"
                              value={company.salesman_name}
                            />
                            <Field
                              label="Contract Date"
                              value={company.contract_date}
                            />
                            <Field
                              label="Billing Email"
                              value={company.billing_email}
                            />
                            <Field label="Postcode" value={company.postcode} />
                            <div className="col-span-2 sm:col-span-3">
                              <Field
                                label="Address"
                                value={[
                                  company.registered_address_line_1,
                                  company.registered_address_line_2,
                                  company.registered_address_line_3,
                                  company.registered_address_line_4,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              />
                            </div>
                          </Section>
                        )}

                        {/* Costs summary */}
                        <div
                          className={`grid grid-cols-2 gap-4 p-4 rounded-lg border ${isDarkMode ? "bg-blue-500/5 border-blue-500/20" : "bg-blue-50 border-blue-200"}`}
                        >
                          <div>
                            <div
                              className={`text-xs mb-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Total Monthly Cost
                            </div>

                            <div className="text-xl font-bold text-blue-400">
                              {(() => {
                                const vals = [
                                  lc.adjusted_rental,
                                  lc.estimated_mobile_charges,
                                  lc.estimated_call_charges,
                                  lc.new_service_charges,
                                  lc.network_settlement_contribution,
                                  lc.leased_line,
                                  lc.total_new_digital_cost,
                                ];
                                if (vals.every((v) => v == null)) return "—";
                                const total = vals.reduce(
                                  (sum, v) => sum + (parseFloat(v) || 0),
                                  0,
                                );
                                return `£${total.toFixed(2)}`;
                              })()}
                            </div>
                          </div>
                          <div>
                            <div
                              className={`text-xs mb-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Total Equipment Cost
                            </div>
                            <div className="text-xl font-bold text-green-400">
                              {fmtCurr(sc.total_equipment_cost)}
                            </div>
                          </div>
                        </div>

                        {/* Notes */}
                        {sc.notes && (
                          <div
                            className={`p-4 rounded-lg border ${isDarkMode ? "bg-yellow-500/5 border-yellow-500/20" : "bg-yellow-50 border-yellow-200"}`}
                          >
                            <div
                              className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? "text-yellow-400" : "text-yellow-600"}`}
                            >
                              Notes
                            </div>
                            <p
                              className={`text-sm whitespace-pre-wrap ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                            >
                              {sc.notes}
                            </p>
                          </div>
                        )}

                        {/* Line requirements */}
                        <Section title="Line Requirements">
                          <Field
                            label="Existing ISDN2"
                            value={
                              sc.line_requirements?.existing_isdn2_channels
                            }
                          />
                          <Field
                            label="New ISDN2 Conversion"
                            value={
                              sc.line_requirements?.new_conversion_isdn2_channel
                            }
                          />
                          <Field
                            label="Existing ISDN30"
                            value={
                              sc.line_requirements?.existing_isdn30_channels
                            }
                          />
                          <Field
                            label="New ISDN30"
                            value={sc.line_requirements?.new_isdn30_channel}
                          />
                        </Section>

                        {/* Current lines */}
                        <Section title="Current Lines">
                          <Field
                            label="Hosted Lifetime"
                            value={sc.current_lines?.hosted_lifetime_licenses}
                          />
                          <Field
                            label="Analogue PSTN"
                            value={sc.current_lines?.analogue_pstn_lines}
                          />
                          <Field
                            label="ADSL/FTTC"
                            value={sc.current_lines?.adsl_fttc_broadband}
                          />
                          <Field
                            label="4G/5G"
                            value={sc.current_lines?.new_4g5g_broadband}
                          />
                          <Field
                            label="FTTP/SOGEA"
                            value={sc.current_lines?.fttp_sogea_broadband}
                          />
                          <Field
                            label="Starlink"
                            value={sc.data_broadband?.starlink_broadband}
                          />
                          <Field
                            label="Leased Lines"
                            value={sc.data_broadband?.leased_lines}
                          />
                        </Section>

                        {/* Service charges */}
                        <Section title="Service Charges">
                          <Field
                            label="Service Charge"
                            value={fmtCurr(charges.service_charge)}
                          />
                          <Field
                            label="Equipment Rentals"
                            value={fmtCurr(charges.equipment_rentals)}
                          />
                          <Field
                            label="Broadband"
                            value={fmtCurr(charges.broadband)}
                          />
                          <Field label="Media" value={fmtCurr(charges.media)} />
                          <Field
                            label="Maintenance"
                            value={fmtCurr(charges.maintenance)}
                          />
                          <Field
                            label="Utilities"
                            value={fmtCurr(charges.utilities)}
                          />
                        </Section>

                        {/* Lease calculator */}
                        {Object.keys(lc).length > 0 && (
                          <Section title="Lease Calculator">
                            <div className="col-span-2 sm:col-span-3">
                              <div>
                                <div
                                  className={`text-xs mb-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  Introductory New Monthly Costs
                                </div>
                                <div className="text-sm font-bold text-green-400">
                                  {(() => {
                                    const vals = [
                                      lc.adjusted_rental,
                                      lc.estimated_mobile_charges,
                                      lc.estimated_call_charges,
                                      lc.new_service_charges,
                                      lc.network_settlement_contribution,
                                      lc.leased_line,
                                      lc.total_new_digital_cost,
                                    ];
                                    if (vals.every((v) => v == null))
                                      return "—";
                                    const total = vals.reduce(
                                      (sum, v) => sum + (parseFloat(v) || 0),
                                      0,
                                    );
                                    return `£${total.toFixed(2)}`;
                                  })()}
                                </div>
                              </div>
                            </div>
                            <Field
                              label="Payment Profile"
                              value={
                                lc.payment_profile
                                  ? `${lc.payment_profile} months`
                                  : "—"
                              }
                            />
                            <Field
                              label="Network Payment"
                              value={
                                lc.network_payment
                                  ? `${lc.network_payment} months`
                                  : "—"
                              }
                            />
                            <Field
                              label="Discount Load"
                              value={lc.discount_load}
                            />
                            <Field
                              label="Telephone System Lease Rental"
                              value={fmtCurr(lc.adjusted_rental)}
                            />
                            <Field
                              label="Estimated Mobile Charges"
                              value={fmtCurr(lc.estimated_mobile_charges)}
                            />
                            <Field
                              label="Estimated. Call Charges"
                              value={fmtCurr(lc.estimated_call_charges)}
                            />
                            <Field
                              label="New Service Charges"
                              value={fmtCurr(lc.new_service_charges)}
                            />
                            <Field
                              label="Leased Line"
                              value={fmtCurr(lc.leased_line)}
                            />

                            {/*
                            <Field
                              label="Actual Lease Payment"
                              value={fmtCurr(lc.actual_lease_payment)}
                            />
                            
                            <Field
                             // label="Actual Profit"
                             // value={fmtCurr(lc.actual_profit)}
                            />*/}
                            <Field
                              label="Sales Rep Commission"
                              value={
                                lc.sales_rep_commission != null
                                  ? `£${lc.sales_rep_commission}`
                                  : "—"
                              }
                            />
                            <Field
                              label="Network Settlement Contribution"
                              value={fmtCurr(
                                lc.network_settlement_contribution,
                              )}
                            />

                            <Field
                              label="Current Total Cost"
                              value={fmtCurr(lc.current_total_cost)}
                            />
                            <Field
                              label="Current Service Charges"
                              value={fmtCurr(lc.current_service_charges)}
                            />
                          </Section>
                        )}

                        {/* Hardware */}
                        {hardwareWithQty.length > 0 && (
                          <div>
                            <h4
                              className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Hardware
                            </h4>
                            <div
                              className={`rounded-lg border divide-y ${isDarkMode ? "bg-gray-800/30 border-gray-700 divide-gray-700" : "bg-gray-50 border-gray-200 divide-gray-200"}`}
                            >
                              {hardwareWithQty.map((h, i) => (
                                <div
                                  key={i}
                                  className="flex items-center justify-between px-4 py-2.5"
                                >
                                  <div>
                                    <span
                                      className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                    >
                                      {h.name}
                                    </span>
                                    <span
                                      className={`text-xs ml-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                    >
                                      {h.category}
                                    </span>
                                  </div>
                                  <span className="text-sm font-semibold text-blue-400">
                                    ×{h.quantity}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Additional hardware */}
                        {additionalWithQty.length > 0 && (
                          <div>
                            <h4
                              className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Additional Hardware
                            </h4>
                            <div
                              className={`rounded-lg border divide-y ${isDarkMode ? "bg-gray-800/30 border-gray-700 divide-gray-700" : "bg-gray-50 border-gray-200 divide-gray-200"}`}
                            >
                              {additionalWithQty.map((h, i) => (
                                <div
                                  key={i}
                                  className="flex items-center justify-between px-4 py-2.5"
                                >
                                  <div>
                                    <span
                                      className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                    >
                                      {h.name}
                                    </span>
                                    <span
                                      className={`text-xs ml-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                    >
                                      {h.category}
                                    </span>
                                  </div>
                                  <span className="text-sm font-semibold text-purple-400">
                                    ×{h.quantity}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Notes */}
            <div
              className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`flex items-center justify-between mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                <h3 className="font-semibold">Notes ({notes.length})</h3>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Note title (optional)"
                  value={newNoteTitle}
                  onChange={(e) => setNewNoteTitle(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg mb-2 focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <textarea
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <div className="flex items-center justify-between mt-2">
                  <label
                    className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <input
                      type="checkbox"
                      checked={isPrivateNote}
                      onChange={(e) => setIsPrivateNote(e.target.checked)}
                      className="rounded"
                    />
                    Private
                  </label>
                  <button
                    onClick={handleCreateNote}
                    disabled={!newNote.trim()}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Note
                  </button>
                </div>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                  >
                    {note.title && (
                      <div
                        className={`font-medium text-sm mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {note.title}
                      </div>
                    )}
                    <p
                      className={`text-sm whitespace-pre-wrap ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                    >
                      {note.body}
                    </p>
                    <div
                      className={`flex items-center justify-between mt-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <span>{note.creator?.name}</span>
                      <span>
                        {new Date(note.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
                {notes.length === 0 && (
                  <p
                    className={`text-sm text-center py-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    No notes yet
                  </p>
                )}
              </div>
            </div>

            {/* Account Info */}
            {opportunity.account && (
              <div
                className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Account Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <label
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Account Name
                    </label>
                    <button
                      onClick={() =>
                        navigate(`/accounts/${opportunity.account.id}`)
                      }
                      className="text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1"
                    >
                      {opportunity.account.name}
                      <ExternalLink size={14} />
                    </button>
                  </div>
                  {opportunity.account.website && (
                    <div>
                      <label
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Website
                      </label>
                      <a
                        href={opportunity.account.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                      >
                        {opportunity.account.website}
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  )}
                  {opportunity.account.industry && (
                    <div>
                      <label
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Industry
                      </label>
                      <p
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {opportunity.account.industry}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditOpportunityModal
          opportunity={opportunity}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchOpportunity();
          }}
        />
      )}

      {showCreateTask && (
        <CreateTaskModal
          opportunity={opportunity}
          onClose={() => setShowCreateTask(false)}
          onSuccess={() => {
            setShowCreateTask(false);
          }}
        />
      )}

      {showBookAppointment && (
        <BookAppointmentModal
          opportunity={opportunity}
          onClose={() => setShowBookAppointment(false)}
          onSuccess={() => {
            setShowBookAppointment(false);
          }}
        />
      )}
      {showQuickQuote && (
        <QuickQuoteModal
          opportunity={opportunity}
          onClose={() => setShowQuickQuote(false)}
          onSuccess={() => {
            setShowQuickQuote(false);
            fetchOpportunity();
          }}
        />
      )}
      {showCloneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Copy className="text-blue-400" size={24} />
              </div>
              <div className="flex-1">
                <h3
                  className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Clone Opportunity
                </h3>
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Create an exact copy of this opportunity. All details
                  including account, contact, products, and status will be
                  preserved.
                </p>
              </div>
            </div>

            <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle
                  size={16}
                  className="text-blue-400 mt-0.5 flex-shrink-0"
                />
                <div>
                  <p className="text-blue-400 text-xs font-medium">
                    What gets cloned?
                  </p>
                  <ul className="text-blue-300 text-xs mt-1 space-y-1">
                    <li>• All opportunity details and dates</li>
                    <li>• Account and contact relationships</li>
                    <li>• All products and pricing</li>
                    <li>• Sales status and checkboxes</li>
                    <li>• Requirements and deal score</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label
                className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                New Opportunity Name
              </label>
              <input
                type="text"
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
                placeholder="Enter name for cloned opportunity"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                autoFocus
              />
              <p
                className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              >
                Leave blank to use "{opportunity.name} (Copy)"
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCloneModal(false);
                  setCloneName("");
                }}
                disabled={isCloning}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                Cancel
              </button>
              <button
                onClick={handleCloneOpportunity}
                disabled={isCloning}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isCloning ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Cloning...
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    Clone
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityDetail;
